import React, { useState } from 'react';
import { CheckSquare as CheckSquareIcon, Check, Paperclip, FileText, Eye } from 'lucide-react';
import FileViewer from './FileViewer';

const ChecklistViewer = ({ content, onComplete, isCompleted, previousResponse, isAdmin = false, children }) => {
    const [checklist, setChecklist] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) return content;
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing checklist content:", e);
            return { items: [] };
        }
    });

    const [completed, setCompleted] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return new Set(parsed.completedItems || []);
            } catch (e) { return new Set(); }
        }
        return new Set();
    });

    React.useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setCompleted(new Set(parsed.completedItems || []));
            } catch (e) { setCompleted(new Set()); }
        } else {
            setCompleted(new Set());
        }
    }, [previousResponse]);

    const [previewFile, setPreviewFile] = useState(null);

    const handleToggle = (itemId) => {
        if (!isCompleted && !isAdmin) {
            setCompleted(prev => {
                const newSet = new Set(prev);
                if (newSet.has(itemId)) newSet.delete(itemId);
                else newSet.add(itemId);
                return newSet;
            });
        }
    };

    const handleCompleteChecklist = () => {
        const requiredItems = checklist.items.filter(item => item.required !== false);
        const allRequiredComplete = requiredItems.every(item => completed.has(item.id));
        if (!allRequiredComplete) {
            alert('Please complete all required items before submitting.');
            return;
        }
        onComplete(JSON.stringify({
            completedItems: Array.from(completed),
            totalItems: checklist.items.length,
            requiredCompleted: requiredItems.length,
            completedAt: new Date().toISOString()
        }));
    };

    if (!checklist.items || checklist.items.length === 0) {
        return (
            <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">
                No checklist items available
            </div>
        );
    }

    const totalItems = checklist.items.length;
    const completedCount = completed.size;
    const requiredItems = checklist.items.filter(item => item.required !== false);
    const allRequiredComplete = requiredItems.every(item => completed.has(item.id));
    const progressPercent = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

    return (
        <div className="space-y-4">
            {/* Progress summary */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">
                    {completedCount} of {totalItems} completed
                </span>
                <span className="text-gray-500 dark:text-slate-400 font-medium">{progressPercent}%</span>
            </div>

            {/* Checklist Items */}
            <div className="space-y-2">
                {checklist.items.map((item, index) => {
                    const isItemCompleted = completed.has(item.id);

                    return (
                        <div
                            key={item.id || index}
                            className={`border rounded-xl px-4 py-3.5 transition-all ${
                                isItemCompleted
                                    ? 'border-green-200 dark:border-green-500/20 bg-green-50/50 dark:bg-green-500/5'
                                    : 'border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900'
                            }`}
                        >
                            <label className={`flex items-start gap-3 ${!isCompleted && !isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
                                <div className="pt-0.5">
                                    <input
                                        type="checkbox"
                                        checked={isItemCompleted}
                                        onChange={() => handleToggle(item.id)}
                                        disabled={isCompleted || isAdmin}
                                        className="w-5 h-5 text-green-600 dark:text-green-500 rounded border-gray-300 dark:border-slate-500 focus:ring-2 focus:ring-green-500 disabled:opacity-50 cursor-pointer"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-[15px] ${
                                        isItemCompleted
                                            ? 'text-gray-400 dark:text-slate-500 line-through'
                                            : 'text-gray-900 dark:text-white'
                                    }`}>
                                        {item.text}
                                    </p>
                                    {item.description && (
                                        <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">{item.description}</p>
                                    )}

                                    {/* Attachments */}
                                    {item.attachments && item.attachments.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {item.attachments.map((file, aIndex) => (
                                                <button
                                                    key={aIndex}
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewFile(file); }}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-md text-xs text-gray-600 dark:text-slate-400 transition-colors"
                                                >
                                                    <FileText className="w-3 h-3" />
                                                    <span className="truncate max-w-[120px]">{file.file_name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </label>
                        </div>
                    );
                })}
            </div>

            {/* Children (Proof/Signature) */}
            {!isCompleted && !isAdmin && children}

            {/* Submit Button */}
            {!isCompleted && !isAdmin && (
                <div className="flex justify-end pt-2">
                    <button
                        onClick={handleCompleteChecklist}
                        disabled={!allRequiredComplete}
                        className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        Submit Checklist
                    </button>
                </div>
            )}

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default ChecklistViewer;
